import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getProvinces } from 'actions/masterData';

import { ProvinceType } from 'models/ProvinceType';
import { RootState } from 'store';
import { Nullable } from 'utils/types/Nullable/types';

export interface ProvincesHook {
  provinces: ProvinceType[],
  getProvinceByNormalizedCode: (
    provinceCode: string | undefined,
    countryCode: string | undefined) => ProvinceType | undefined;
  getProvinceByName: (name: string) => Nullable<ProvinceType>
}

export const useProvinces = (): ProvincesHook => {
  const dispatch = useDispatch();
  const { items, normalized } = useSelector(
    (state: RootState) => ({
      items: state.masterData.provinces,
      normalized: state.normalized.provinces,
    }),
  );

  const getProvinceByNormalizedCode = useCallback(
    (provinceCode: string | undefined, countryCode: string | undefined) => {
      const normalizedProvinceCode = `${countryCode}${provinceCode}`;
      return provinceCode && countryCode
        && normalized[normalizedProvinceCode]
        ? items.find(
          (province:
            ProvinceType) => province.code === provinceCode && province.countryCode === countryCode,
        )
        : undefined;
    },
    [normalized, items],
  );

  const getProvinceByName = useCallback(
    (name: string) => {
      const getByName = (i: ProvinceType) => i.description?.toUpperCase() === name?.toUpperCase();
      const provinces = items?.filter(getByName);

      if (provinces?.length === 0) return null;
      return provinces?.length === 1 ? provinces.find(getByName) : null;
    },
    [items],
  );

  useEffect(() => {
    dispatch(getProvinces());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    getProvinceByName,
    getProvinceByNormalizedCode,
    provinces: items || [],
  };
};
